"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { RedirectToSignIn } from "@daveyplate/better-auth-ui";
import { Archive, ArchiveRestore, BookOpen, ExternalLink, MessageSquare, Star } from "lucide-react";
import { GameUserCommentEditor } from "./GameUserCommentEditor";
import { encodeOpeningViewerMovesParam } from "~/utils/openingContext";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import Spinner from "~/components/ui/spinner";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { useToast } from "~/hooks/use-toast";
import { buildOpeningLookup, projectGameAnalysisList, resolveDateWindow, type ListSort, type ListDateFilter, type ListFilters } from "./listProjection";
import { analysisChoices, colorChoices, commentPresenceChoices, ListFilterControls, PreparationAnalysisFilter, scoreChoices, StudentColorFilter, StudentScoreFilter, UserCommentPresenceFilter } from "./ListFilterControls";
import { ListDateControls, type DateDraft } from "./ListDateControls";
import { OpeningFilterEditor } from "./OpeningFilterEditor";
import { HeaderFilter } from "./HeaderFilter";
import { preparationAnalysisOutcomes } from "./metadata";
import { useGameAnalysisCollection } from "./useGameAnalysisCollection";
import { useGameAnalysisRowFocus } from "./useGameAnalysisRowFocus";
import { useGameAnalysisAuthorization } from "./useGameAnalysisAuthorization";
import {
  GameAnalysisImportDialog,
  useGameAnalysisImportDraft,
} from "./GameAnalysisImportDialog";

const columns = [
  { column: "studentColor", label: "Цвет" },
  { column: "title", label: "Название" },
  { column: "uploadedAt", label: "Дата и время" },
  { column: "studentScore", label: "Результат" },
  { column: "opening", label: "Дебют" },
  { column: "analysis", label: "Результат анализа и комментарий" },
] as const;

function UnknownValue({ description }: { description: string }) {
  return <span aria-label={description} className="text-muted-foreground">
    <span aria-hidden="true">—</span><span className="sr-only">{description}</span>
  </span>;
}

function formatLocalDate(timestamp: number): string {
  const value = new Date(timestamp);
  const year = value.getFullYear();
  return `${String(value.getDate()).padStart(2, "0")}.${String(value.getMonth() + 1).padStart(2, "0")}.${year < 0 ? "-" : ""}${String(Math.abs(year)).padStart(4, "0")}`;
}

export default function GameAnalysisList({ selected }: { selected: boolean }) {
  const router = useRouter();
  const access = useGameAnalysisAuthorization();
  const [commentGame, setCommentGame] = useState<{ problemId: number; title: string; userComment: string | null; generation: object; returnFocus: () => void } | null>(null);
  const currentCommentGame = useRef(commentGame);
  currentCommentGame.current = commentGame;
  const { toast } = useToast();
  const unavailableNotification = useRef<ReturnType<typeof toast>>();
  useLayoutEffect(() => () => {
    unavailableNotification.current?.dismiss();
    unavailableNotification.current = undefined;
  }, [selected, access.generation]);
  const endUnavailableEditor = (editor: NonNullable<typeof commentGame>) => {
    if (currentCommentGame.current !== editor) return;
    setCommentGame(null);
    unavailableNotification.current = toast({ title: "Партия недоступна" });
  };
  const { loaded, failure, needsRefresh, saving, rowErrors, recovering, saveFlag, saveComment, refreshList, startImport } = useGameAnalysisCollection(selected, access, (data) => {
    const editor = currentCommentGame.current;
    if (editor && !data.games.some((game) => game.problemId === editor.problemId)) endUnavailableEditor(editor);
  });
  const { tableHost, titleFilterButton, trackRemoval, trackModal } = useGameAnalysisRowFocus(selected, access);
  const { generation, allowed } = access;
  const openings = useMemo(() => buildOpeningLookup(loaded?.data.openingGroups ?? []), [loaded]);
  const [query, setQuery] = useState("");
  if (commentGame && (!selected || commentGame.generation !== generation)) setCommentGame(null);
  const [openingQuery, setOpeningQuery] = useState("");
  const [filters, setFilters] = useState<ListFilters>({ showArchived: false });
  const [date, setDate] = useState<ListDateFilter>(() => ({ mode: "last-days", days: 10, anchor: new Date() }));
  const [dateDraft, setDateDraft] = useState<DateDraft>({ mode: "last-days", days: "10", start: "", end: "" });
  const [invalidDate, setInvalidDate] = useState(false);
  const changeDate = (draft: DateDraft) => {
    setDateDraft(draft);
    const candidate: ListDateFilter = draft.mode === "all" ? { mode: "all" }
      : draft.mode === "interval" ? { mode: "interval", start: draft.start, end: draft.end }
      : { mode: "last-days", days: Number(draft.days), anchor: new Date() };
    try {
      resolveDateWindow(candidate);
      setDate(candidate);
      setInvalidDate(false);
    } catch {
      setInvalidDate(true);
    }
  };
  const [sort, setSort] = useState<ListSort>({ column: "uploadedAt", direction: "desc" });
  const resetView = () => {
    setQuery("");
    setOpeningQuery("");
    setFilters({ showArchived: false });
    setSort({ column: "uploadedAt", direction: "desc" });
    setDateDraft({ mode: "last-days", days: "10", start: "", end: "" });
    setDate({ mode: "last-days", days: 10, anchor: new Date() });
    setInvalidDate(false);
  };
  const confirmedOwner = access.status === "forbidden" ? null : access.identityOwnerId;
  const viewOwner = useRef(confirmedOwner);
  if (
    confirmedOwner !== undefined &&
    viewOwner.current !== confirmedOwner
  ) {
    viewOwner.current = confirmedOwner;
    resetView();
  }
  const importDraft = useGameAnalysisImportDraft(selected, access);
  const manuallyRefreshList = () => {
    if (!access.isCurrent()) return;
    if (date.mode === "last-days") {
      const candidate = { ...date, anchor: new Date() };
      try {
        resolveDateWindow(candidate);
        setDate(candidate);
      } catch {
        setInvalidDate(true);
      }
    }
    refreshList();
  };

  if (!selected) return null;
  if (access.status === "error") {
    return (
      <div role="alert">
        Не удалось проверить доступ{" "}
        <Button onClick={access.retry}>Повторить</Button>
      </div>
    );
  }
  if (access.status === "forbidden") return <p>Доступ к разделу запрещён</p>;
  const loading = (
    <div role="status" aria-label="Загрузка партий">
      <Spinner style={{ width: 40, height: 40 }} />
    </div>
  );
  if (access.status === "pending") return loading;
  if (!allowed) return access.status === "anonymous" ? <RedirectToSignIn /> : null;
  const data = failure !== generation && loaded?.generation === generation
    ? loaded.data
    : undefined;
  const filtered = projectGameAnalysisList(data?.games ?? [], { query, sort, openings, date, filters });
  const dateWindow = resolveDateWindow(date);
  const appliedPeriod = dateWindow ? `${formatLocalDate(dateWindow.startInclusive)}–${formatLocalDate(dateWindow.endExclusive - 1)}` : "Все даты";
  const dateSummary = date.mode === "all" ? undefined : date.mode === "last-days" ? `Последние ${date.days} дней` : appliedPeriod;
  const selectedOpeningIds = filters.openings?.ids ?? [];
  const openingSummary = [
    selectedOpeningIds.length === 1 && data
      ? openings.get(selectedOpeningIds[0]!)?.title ?? "Недоступный дебют"
      : selectedOpeningIds.length > 0 ? `Дебютов: ${selectedOpeningIds.length}` : "",
    filters.openings?.withoutOpening ? "Без дебюта" : "",
    filters.openings?.unavailable ? "Недоступные дебюты" : "",
  ].filter(Boolean).join(" · ") || undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-2">
        <Button onClick={manuallyRefreshList}>Обновить список</Button>
        <Button variant="outline" onClick={resetView}>Сбросить</Button>
        <GameAnalysisImportDialog
          draft={importDraft}
          onInspectList={manuallyRefreshList}
          onImported={refreshList}
          onWriteStarted={startImport}
        />
      </div>
      <div ref={tableHost} className="min-h-0 flex-1 overflow-auto rounded-md border">
        <table aria-label="Список партий" className="w-full min-w-[1152px] table-fixed caption-bottom text-sm">
          {/* Fixed utility columns leave the remaining space to title/opening/analysis in a 40:25:35 ratio. */}
          <colgroup>
            <col className="w-[80px]" />
            <col className="w-[40%]" />
            <col className="w-[148px]" />
            <col className="w-[112px]" />
            <col className="w-[25%]" />
            <col className="w-[35%]" />
            <col className="w-[52px]" />
            <col className="w-[229px]" />
          </colgroup>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              {columns.map(({ column, label }) => {
                const heading = <button type="button" className="inline-flex min-w-0 items-center gap-1 whitespace-normal text-left leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setSort((current) => ({ column, direction: current.column === column ? (current.direction === "asc" ? "desc" : "asc") : column === "uploadedAt" ? "desc" : "asc" }))}>
                  {label}{sort.column === column && <span aria-hidden="true">{sort.direction === "asc" ? "↑" : "↓"}</span>}
                </button>;
                return <TableHead key={column} className={column === "studentColor" || column === "studentScore" || column === "uploadedAt" ? "px-1 py-2 align-top" : "py-2 align-top"} aria-sort={sort.column === column ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
                {column === "title" && <HeaderFilter key={access.ownerId} heading={heading} triggerRef={titleFilterButton} label={label} summary={query ? `«${query}»` : undefined} onClear={() => setQuery("")}>
                  <Input type="search" aria-label="Поиск по названию партии"
                    placeholder="Фильтр по названию" value={query} onChange={(event) => setQuery(event.target.value)} />
                </HeaderFilter>}
                {column === "uploadedAt" && <HeaderFilter key={access.ownerId} heading={heading} compact label={label}
                  summary={dateSummary} description={date.mode === "last-days" ? `${dateSummary} · ${appliedPeriod}` : dateSummary}
                  onClear={() => changeDate({ mode: "all", days: "10", start: "", end: "" })}>
                  <p className="mb-3 text-xs text-muted-foreground">Применённый период: <span aria-label="Применённый период" className="mt-1 block whitespace-nowrap text-sm text-popover-foreground">{appliedPeriod}</span></p>
                  <ListDateControls draft={dateDraft} invalid={invalidDate} onChange={changeDate} />
                </HeaderFilter>}
                {column === "studentColor" && <HeaderFilter key={access.ownerId} heading={heading} compact label={label}
                  summary={filters.color && filters.color !== "all" ? colorChoices.find(([value]) => value === filters.color)?.[1] : undefined}
                  onClear={() => setFilters({ ...filters, color: "all" })}>
                  <StudentColorFilter filters={filters} onChange={setFilters} />
                </HeaderFilter>}
                {column === "studentScore" && <HeaderFilter key={access.ownerId} heading={heading} compact label={label}
                  summary={filters.score !== undefined && filters.score !== "all" ? scoreChoices.find(([value]) => value === String(filters.score))?.[1] : undefined}
                  onClear={() => setFilters({ ...filters, score: "all" })}>
                  <StudentScoreFilter filters={filters} onChange={setFilters} />
                </HeaderFilter>}
                {column === "opening" && <HeaderFilter key={access.ownerId} heading={heading} label={label} preferredWidth={350} summary={openingSummary} onClear={() => { setFilters({ ...filters, openings: { ids: [] } }); setOpeningQuery(""); }}>
                  <OpeningFilterEditor groups={data?.openingGroups} openings={openings} selection={filters.openings}
                    onChange={(selection) => setFilters({ ...filters, openings: selection })} query={openingQuery} onQueryChange={setOpeningQuery} />
                </HeaderFilter>}
                {column === "analysis" && <HeaderFilter key={access.ownerId} heading={heading} label={label}
                  summary={filters.analysis && filters.analysis !== "all" ? analysisChoices.find(([value]) => value === filters.analysis)?.[1] : undefined}
                  onClear={() => setFilters({ ...filters, analysis: "all" })}>
                  <PreparationAnalysisFilter filters={filters} onChange={setFilters} />
                </HeaderFilter>}
              </TableHead>; })}
              <TableHead className="px-1 py-2 align-top" aria-label="Комментарий пользователя">
                <HeaderFilter key={access.ownerId} heading={<MessageSquare aria-hidden="true" className="size-4" />} compact label="Комментарий пользователя"
                  summary={filters.commentPresence && filters.commentPresence !== "all" ? commentPresenceChoices.find(([value]) => value === filters.commentPresence)?.[1] : undefined}
                  onClear={() => setFilters({ ...filters, commentPresence: "all" })}>
                  <UserCommentPresenceFilter filters={filters} onChange={setFilters} />
                </HeaderFilter>
              </TableHead>
              <TableHead className="px-1 py-2 align-top">
                <HeaderFilter key={access.ownerId} heading="Действия" compact label="Действия"
                  summary={[filters.showArchived === true ? "Архив" : "", filters.favoritesOnly ? "Избранные" : ""].filter(Boolean).join(" · ") || undefined}
                  onClear={() => setFilters({ ...filters, showArchived: true, favoritesOnly: false })}>
                  <ListFilterControls filters={filters} onChange={setFilters} />
                </HeaderFilter>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data && <TableRow><TableCell colSpan={8} className="py-6 text-center">
              {failure === generation ? <div role="alert">
                {(importDraft.report?.importedCount ?? 0) > 0
                  ? "Партии импортированы, но не удалось обновить список"
                  : "Не удалось загрузить партии"}{" "}
                {(importDraft.report?.importedCount ?? 0) === 0 && <Button onClick={manuallyRefreshList}>Повторить</Button>}
              </div> : needsRefresh === generation ? <p>Требуется обновить список</p> : loading}
            </TableCell></TableRow>}
            {data && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-muted-foreground">
                  {data.games.length === 0 ? "Партий пока нет" : "Партии не найдены"}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((game) => {
              const writeBlocked = saving.has(game.problemId) || recovering.has(game.problemId);
              const commentLabel = game.userComment === null ? "Добавить комментарий" : "Редактировать комментарий";
              const outcome = preparationAnalysisOutcomes.find(({ code }) => code === game.analysis?.outcome);
              const opening = game.openingId === null ? undefined : openings.get(game.openingId);
              const openGame = () => {
                if (access.isCurrent()) router.push(`/problem-sets/${game.problemSetId}/problems/${game.problemId}/viewer?context=game-analysis&focus=analysis`);
              };
              return (
              <TableRow key={game.problemId} data-game-analysis-id={game.problemId}>
                <TableCell className="px-1">{game.studentColor === null
                  ? <UnknownValue description="Цвет игрока неизвестен" />
                  : <span role="img" aria-label={game.studentColor === "white" ? "Белые" : "Чёрные"} className={`inline-block size-4 rounded-sm border border-gray-500 ${game.studentColor === "white" ? "bg-white" : "bg-black"}`} />}</TableCell>
                <TableCell>
                  <button
                    type="button"
                    className="block min-w-0 whitespace-normal break-words text-left text-sm leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={openGame}
                  >{game.title}</button>
                  {game.archived && <span className="text-xs text-muted-foreground">Архив</span>}
                </TableCell>
                <TableCell className="px-1">{game.uploadedAt === null
                  ? <UnknownValue description="Время загрузки неизвестно" />
                  : <time className="whitespace-nowrap" dateTime={game.uploadedAt} title={game.uploadedAt} aria-label={`Время загрузки: ${game.uploadedAt}`}>
                    {new Intl.DateTimeFormat("ru-RU", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(game.uploadedAt))}
                  </time>}</TableCell>
                <TableCell className="px-1">{game.studentScore === null
                  ? <UnknownValue description="Результат партии неизвестен" />
                  : <span className={`font-bold ${game.studentScore === 1 ? "text-green-600" : game.studentScore === 0 ? "text-red-600" : "text-yellow-600"}`}>{game.studentScore === 0.5 ? "1/2" : game.studentScore}</span>}</TableCell>
                <TableCell>{game.openingId === null
                  ? <span className="text-muted-foreground">—</span>
                  : openings.get(game.openingId)?.title ?? <span className="text-muted-foreground">Дебют недоступен</span>}</TableCell>
                <TableCell className="whitespace-normal break-words">{game.analysis === null
                  ? <UnknownValue description="Результат анализа отсутствует" />
                  : <>
                    <span className={outcome?.className}>{outcome?.label}</span>
                    {game.analysis.comment && <> <span className="whitespace-pre-wrap break-words text-xs font-normal not-italic text-muted-foreground">({game.analysis.comment})</span></>}
                  </>}</TableCell>
                <TableCell className="px-1"><Button data-user-comment-entry variant="ghost" size="icon" className="h-11 w-11" disabled={writeBlocked} aria-label={commentLabel} title={commentLabel} onClick={(event) => { if (access.isCurrent()) setCommentGame({ ...game, generation, returnFocus: trackModal(event.currentTarget) }); }}><MessageSquare className="size-4" fill={game.userComment === null ? "none" : "currentColor"} /></Button></TableCell>
                <TableCell className="px-1">
                  <div className="flex gap-2 [&>button]:h-11 [&>button]:w-11 [&>button]:shrink-0">
                    <Button variant="ghost" size="icon" aria-label="Открыть" title="Открыть" onClick={openGame}><ExternalLink className="size-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label={game.favorite ? "Убрать из избранного" : "Добавить в избранное"} title={game.favorite ? "Убрать из избранного" : "Добавить в избранное"} disabled={writeBlocked} onClick={(event) => saveFlag(game.problemId, "favorite", !game.favorite, trackRemoval(event.currentTarget))}><Star className={`size-4 ${game.favorite ? "fill-current" : ""}`} /></Button>
                    <Button variant="ghost" size="icon" aria-label="Открыть дебют" title="Открыть дебют" disabled={!opening} onClick={() => {
                      if (opening && access.isCurrent()) router.push(`/problem-sets/${opening.problemSetId}/problems/${opening.problemId}/viewer?moves=${encodeURIComponent(encodeOpeningViewerMovesParam([]))}`);
                    }}><BookOpen className="size-4" /></Button>
                    <Button variant="ghost" size="icon" className="ml-[21px]" aria-label={game.archived ? "Из архива" : "В архив"} title={game.archived ? "Из архива" : "В архив"} disabled={writeBlocked} onClick={(event) => saveFlag(game.problemId, "archived", !game.archived, trackRemoval(event.currentTarget))}>{game.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}</Button>
                  </div>
                  {saving.has(game.problemId) && <span role="status" className="text-xs text-muted-foreground">Сохранение…</span>}
                  {rowErrors.get(game.problemId) && <p role="alert" className="text-xs text-destructive">{rowErrors.get(game.problemId)}</p>}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </table>
      </div>
      {commentGame && <GameUserCommentEditor key={commentGame.problemId} title={commentGame.title} initialSource={commentGame.userComment} pending={saving.has(commentGame.problemId)} recovering={recovering.has(commentGame.problemId)} readFailed={failure === generation} onRefresh={manuallyRefreshList} onSave={(value, onResult) => saveComment(commentGame.problemId, value, (result) => { if (currentCommentGame.current !== commentGame) return; if (result.status === "unavailable") { endUnavailableEditor(commentGame); return; } onResult(result); })} onDismiss={() => setCommentGame(null)} onReturnFocus={commentGame.returnFocus} />}
    </div>
  );
}
